import { getBrowserStorage } from './getBrowserStorage';
import { createStorageKey } from './internal/createStorageKey';
import { dispatchStorageEvent } from './internal/dispatchStorageEvent';
import type { StorageType } from './internal/StorageType';
import { defaultStorageType } from './internal/StorageType';
import type { StorageSlot } from './StorageSlot';
import { noopStorageSlot } from './StorageSlot';

export function createStorageSlot(
  key: string,
  { storageType, isEnabled }: { storageType?: StorageType; isEnabled?: boolean } = { storageType: defaultStorageType },
): StorageSlot {
  if (isEnabled === false) {
    return noopStorageSlot;
  }

  const storageKey = createStorageKey(key);
  const storage = getBrowserStorage(storageType);
  if (storage === undefined) {
    return noopStorageSlot;
  }

  return {
    get: () => {
      try {
        return storage.getItem(storageKey);
      } catch {
        return null;
      }
    },
    set: (newValue) => {
      try {
        const oldValue = storage.getItem(storageKey);
        storage.setItem(storageKey, newValue);
        dispatchStorageEvent({
          key: storageKey,
          oldValue,
          newValue,
          storage,
        });
      } catch {
        // noop
      }
    },
    remove: () => {
      try {
        const oldValue = storage.getItem(storageKey);
        storage.removeItem(storageKey);
        dispatchStorageEvent({ key: storageKey, oldValue, newValue: null, storage });
      } catch {
        // noop
      }
    },
    subscribe: (onChange) => {
      try {
        const listener = (event: StorageEvent) => {
          if (event.storageArea === storage && event.key === storageKey) {
            onChange(event);
          }
        };
        window.addEventListener('storage', listener);
        return () => window.removeEventListener('storage', listener);
      } catch {
        return () => {};
      }
    },
  };
}
